import { createSelector } from '@reduxjs/toolkit';
import { RootState } from '../../../app/store';
import { SessionStatus } from '../sessionBoundary/sessionBoundary.constants';
import { resolveIssueReportSessionId } from '../../../utils/issueReportContext';
import { IssueReportRecord } from './issueReport.props';

const selectIssueReport = (state: RootState) => state.issueReport;

/** Reports in the order they were filed. */
const selectIssueReports = createSelector(
  selectIssueReport,
  (state): IssueReportRecord[] =>
    state.order.map((id) => state.byId[id]).filter(Boolean)
);

/** The record inside the open flag window — what the panel acts on. */
const selectActiveIssueReport = createSelector(
  selectIssueReport,
  (state): IssueReportRecord | null =>
    state.activeReportId ? state.byId[state.activeReportId] ?? null : null
);

const selectActiveFlagWindowExpiresAt = createSelector(
  selectIssueReport,
  (state) => state.activeWindowExpiresAt
);

/** Banked remainder while paused; null while the countdown is running. */
const selectFlagWindowRemainingMs = createSelector(
  selectIssueReport,
  (state) => state.remainingMs
);

/**
 * Is a session actually RUNNING right now.
 *
 * Not derivable from the session id, which is why this exists separately.
 * `fillEndSessionValues` clears `sessionBoundary.sessionId` on end but moves it to
 * `endedSessionId` — and `taskRouter.taskAssignedSessionId` is not cleared until the
 * agent leaves for the lobby. So after a car departs, BOTH remaining sources still
 * resolve to the session that just ended, and a count filtered on the id alone kept
 * showing the departed car's total for the whole gap until the next car arrived.
 *
 * `sessionStatus` is the only unambiguous signal: `start` while running, `end` after,
 * `''` before anything has begun.
 */
const selectIsSessionLive = (state: RootState) =>
  state.sessionBoundary?.sessionStatus === SessionStatus.start;

/**
 * Reports filed during the CURRENT session, oldest first.
 *
 * A FILTER, not a counter — and that distinction is the whole design. Records
 * deliberately survive session teardown (only `user/logout` wipes them), so there is
 * nothing to reset at a session boundary: the next session resolves to a new id,
 * no record matches, and the count is empty. That is exactly what makes the button
 * badge "reset to null, not zero" for free, with no reset code to get wrong.
 *
 * Matches the FROZEN `envelopeIdentity.session_id` against the live one via the
 * shared `resolveIssueReportSessionId`. Both sides must use that one function — see
 * its note.
 *
 * A null current session id matches NOTHING, including records that also froze null.
 * "No session" is not a session, and grouping every session-less report together
 * would show an agent a running total spanning unrelated cars.
 *
 * Voided reports are excluded: the agent retracted it, so counting it back at them
 * would be wrong.
 *
 * Empty whenever no session is LIVE — see `selectIsSessionLive`. Both surfaces this
 * feeds answer "for the car in front of you", and once that car has gone the honest
 * answer is nothing rather than the last one's total.
 *
 * CONSEQUENCE, accepted: a flag filed in the gap after the car leaves is still
 * recorded against the session it is about — the freeze path deliberately keeps
 * `endedSessionId` — but it shows no badge and no summary line, because there is no
 * live session for either to describe.
 */
const selectSessionIssueReports = createSelector(
  selectIssueReports,
  resolveIssueReportSessionId,
  selectIsSessionLive,
  (reports, sessionId, isLive): IssueReportRecord[] =>
    isLive && sessionId
      ? reports.filter(
          (report) =>
            !report.voided && report.envelopeIdentity.session_id === sessionId
        )
      : []
);

/**
 * What the button badge shows. INCLUDES the report whose window is currently open —
 * the agent has filed it, so "how many this session" counts it.
 */
const selectSessionIssueReportCount = createSelector(
  selectSessionIssueReports,
  (reports) => reports.length
);

/**
 * The same set, newest first — the order both surfaces render in, and what the
 * button's tooltip lists. Memoized here rather than reversed at each call site: a
 * fresh array on every render would defeat the memoization downstream of it.
 */
const selectSessionIssueReportsNewestFirst = createSelector(
  selectSessionIssueReports,
  (reports): IssueReportRecord[] => reports.slice().reverse()
);

/**
 * What the panel's summary line lists: this session's reports EXCLUDING the one the
 * panel is currently about, newest first.
 *
 * Excluding the active one is not a detail — a panel that lists the report you are
 * looking at as "earlier this session" reads as broken. The BUTTON's tooltip keeps
 * it, because there the count says 4 and the list has to show 4.
 */
const selectPriorSessionIssueReports = createSelector(
  selectSessionIssueReportsNewestFirst,
  selectIssueReport,
  (reports, state): IssueReportRecord[] =>
    reports.filter((report) => report.issue_id !== state.activeReportId)
);

export {
  selectActiveFlagWindowExpiresAt,
  selectActiveIssueReport,
  selectIssueReport,
  selectIssueReports,
  selectFlagWindowRemainingMs,
  selectPriorSessionIssueReports,
  selectSessionIssueReportCount,
  selectSessionIssueReports,
  selectSessionIssueReportsNewestFirst,
};
